"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.debtsReportResponse = exports.debtsReportQuery = exports.treasuryMovementResponse = exports.treasuryMovementCategory = exports.treasuryMovementQuery = exports.purchasesAnalysisResponse = exports.purchasesAnalysisQuery = exports.purchasesGroupBy = exports.salesAnalysisResponse = exports.salesAnalysisQuery = exports.salesGroupBy = exports.documentStatusFilter = void 0;
const zod_1 = require("zod");
const common_1 = require("./common");
/**
 * مركز التقارير — تقارير تشغيلية مرنة بالفترة: المبيعات والمشتريات وحركة النقدية
 * والمديونية. كلها قراءة من المستندات والقيود المنشورة، والمبالغ نصوص عشرية تُجمع فى SQL.
 *
 * كل تقرير يحمل رقم مطابقة مع الدفتر حيث يوجد مقابل له، فلا يبتعد التقرير التشغيلى
 * عن القوائم المالية بصمت.
 */
const period = { from: common_1.isoDate, to: common_1.isoDate };
const periodOrder = (value) => value.from <= value.to;
const periodIssue = { message: 'from must not be after to', path: ['from'] };
/** حال الفاتورة فى نهاية الفترة — من مستنداتها حتى ذلك التاريخ. */
exports.documentStatusFilter = zod_1.z.enum(['all', 'open', 'settled', 'cancelled']);
// ---------------------------------------------------------------------------
// المبيعات
// ---------------------------------------------------------------------------
exports.salesGroupBy = zod_1.z.enum(['day', 'month', 'customer', 'item']);
exports.salesAnalysisQuery = zod_1.z
    .object({
    ...period,
    groupBy: exports.salesGroupBy.default('month'),
    customerId: common_1.uuid.optional(),
    itemId: common_1.uuid.optional(),
    status: exports.documentStatusFilter.default('all'),
})
    .refine(periodOrder, periodIssue);
const salesFigures = {
    /** عدد الفواتير الصادرة فى الفترة داخل المجموعة. */
    invoiceCount: zod_1.z.number().int(),
    /** صافى الفواتير الصادرة قبل الضريبة. */
    invoices: common_1.moneyString,
    /** فواتير أُلغيت فى الفترة — تُخصم بتاريخ الإلغاء كقيدها العكسى. */
    cancellations: common_1.moneyString,
    /** إشعارات دائنة (مردودات وتخفيضات) — لا نوع لها يفصل المرتجع عن الخصم بعد. */
    creditNotes: common_1.moneyString,
    debitNotes: common_1.moneyString,
    /** الفواتير − الإلغاء − الدائنة + المدينة: نفس أثرها على حسابات الإيراد. */
    netSales: common_1.moneyString,
    /** صافى ضريبة القيمة المضافة على نفس الحركة. */
    vat: common_1.moneyString,
    /** صافى المبيعات + الضريبة. */
    total: common_1.moneyString,
    /**
     * المحصّل والمتبقى من فواتير صدرت فى الفترة، حتى نهايتها. `null` فى التجميع بالصنف
     * أو التصفية به: التحصيل يُخصص على الفاتورة لا على سطرها.
     */
    collected: common_1.moneyString.nullable(),
    outstanding: common_1.moneyString.nullable(),
};
exports.salesAnalysisResponse = zod_1.z.object({
    companyId: common_1.uuid,
    from: common_1.isoDate,
    to: common_1.isoDate,
    groupBy: exports.salesGroupBy,
    rows: zod_1.z.array(zod_1.z.object({
        /** مفتاح المجموعة: تاريخ أو شهر أو معرّف — `none` لسطر بلا صنف. */
        key: zod_1.z.string(),
        label: zod_1.z.string(),
        code: zod_1.z.string().nullable(),
        ...salesFigures,
    })),
    totals: zod_1.z.object(salesFigures),
    /** إيرادات الفترة فى قائمة الدخل (بلا قيد الإقفال). */
    ledgerRevenue: common_1.moneyString,
    /**
     * إيرادات الدفتر − صافى المبيعات: قيود إيراد لا تخص مستند مبيعات (يدوية مثلاً).
     * `null` حين توجد تصفية — المقارنة بالدفتر لا تكون إلا للكل.
     */
    unreconciled: common_1.moneyString.nullable(),
});
// ---------------------------------------------------------------------------
// المشتريات
// ---------------------------------------------------------------------------
/** فواتير الموردين بلا صنف على سطورها، فمكان الصنف هنا حساب السطر (المصروف أو الأصل). */
exports.purchasesGroupBy = zod_1.z.enum(['day', 'month', 'supplier', 'account']);
exports.purchasesAnalysisQuery = zod_1.z
    .object({
    ...period,
    groupBy: exports.purchasesGroupBy.default('month'),
    supplierId: common_1.uuid.optional(),
    accountCode: common_1.accountCode.optional(),
    status: exports.documentStatusFilter.default('all'),
})
    .refine(periodOrder, periodIssue);
const purchasesFigures = {
    billCount: zod_1.z.number().int(),
    bills: common_1.moneyString,
    cancellations: common_1.moneyString,
    /** إشعارات مدينة نصدرها للمورد (مرتجع أو خصم) — تُخصم. */
    debitNotes: common_1.moneyString,
    /** إشعارات دائنة منه (زيادة) — تُضاف. */
    creditNotes: common_1.moneyString,
    netPurchases: common_1.moneyString,
    vat: common_1.moneyString,
    total: common_1.moneyString,
    /** المسدد والمتبقى من فواتير الفترة حتى نهايتها — `null` فى التجميع أو التصفية بالحساب. */
    paid: common_1.moneyString.nullable(),
    outstanding: common_1.moneyString.nullable(),
};
exports.purchasesAnalysisResponse = zod_1.z.object({
    companyId: common_1.uuid,
    from: common_1.isoDate,
    to: common_1.isoDate,
    groupBy: exports.purchasesGroupBy,
    rows: zod_1.z.array(zod_1.z.object({
        key: zod_1.z.string(),
        label: zod_1.z.string(),
        code: zod_1.z.string().nullable(),
        ...purchasesFigures,
    })),
    totals: zod_1.z.object(purchasesFigures),
});
// ---------------------------------------------------------------------------
// حركة النقدية
// ---------------------------------------------------------------------------
exports.treasuryMovementQuery = zod_1.z
    .object({
    ...period,
    /** حساب خزينة أو بنك بعينه — بدونه كل حسابات النقدية. */
    accountCode: common_1.accountCode.optional(),
})
    .refine(periodOrder, periodIssue);
/** تصنيف الحركة بمستندها — للعرض فقط، والرصيد من المدين والدائن. */
exports.treasuryMovementCategory = zod_1.z.enum([
    'customer_receipt',
    'supplier_payment',
    'expense',
    'treasury_transfer',
    'opening_balance',
    'other',
]);
const treasuryFigures = {
    openingBalance: common_1.moneyString,
    /** وارد من غير التحويلات (تحصيل، أرصدة افتتاحية، قيود يدوية…). */
    receipts: common_1.moneyString,
    /** منصرف من غير التحويلات (سداد موردين، مصروفات…). */
    payments: common_1.moneyString,
    transfersIn: common_1.moneyString,
    transfersOut: common_1.moneyString,
    /** الافتتاحى + الوارد + المحوَّل إليه − المنصرف − المحوَّل منه = رصيد الحساب فى الدفتر. */
    closingBalance: common_1.moneyString,
};
exports.treasuryMovementResponse = zod_1.z.object({
    companyId: common_1.uuid,
    from: common_1.isoDate,
    to: common_1.isoDate,
    accounts: zod_1.z.array(zod_1.z.object({
        accountCode: common_1.accountCode,
        accountNameAr: zod_1.z.string(),
        kind: zod_1.z.enum(['cashbox', 'bank']),
        /** أسماء الخزائن أو الحسابات البنكية المربوطة بالحساب. */
        holders: zod_1.z.array(zod_1.z.string()),
        ...treasuryFigures,
        lines: zod_1.z.array(zod_1.z.object({
            journalId: common_1.uuid,
            journalNumber: zod_1.z.string().nullable(),
            lineNumber: zod_1.z.number().int(),
            postingDate: common_1.isoDate,
            category: exports.treasuryMovementCategory,
            sourceType: zod_1.z.string(),
            sourceId: zod_1.z.string().nullable(),
            reference: zod_1.z.string().nullable(),
            memo: zod_1.z.string().nullable(),
            /** الطرف (عميل أو مورد) على قيد الحركة إن وُجد. */
            counterparty: zod_1.z.string().nullable(),
            debit: common_1.moneyString,
            credit: common_1.moneyString,
            runningBalance: common_1.moneyString,
        })),
    })),
    /** التحويلات بين حسابين ضمن التقرير تظهر فى الجانبين، فتتقاص فى الإجمالى. */
    totals: zod_1.z.object(treasuryFigures),
});
// ---------------------------------------------------------------------------
// المديونية
// ---------------------------------------------------------------------------
exports.debtsReportQuery = zod_1.z.object({
    party: zod_1.z.enum(['customers', 'suppliers']).default('customers'),
    asOf: common_1.isoDate,
    overdueOnly: zod_1.z
        .enum(['true', 'false'])
        .default('false')
        .transform((value) => value === 'true'),
    /** أقل رصيد يظهر — بالقيمة المطلقة، فالرصيد الدائن الكبير يظهر كذلك. */
    minBalance: common_1.moneyString.optional(),
    sort: zod_1.z.enum(['largest', 'overdue', 'code']).default('largest'),
});
const debtFigures = {
    current: common_1.moneyString,
    /** المتأخر عن الاستحقاق: كل الشرائح بعد «حالية». */
    overdue: common_1.moneyString,
    over90: common_1.moneyString,
    unappliedCredit: common_1.moneyString,
    total: common_1.moneyString,
};
exports.debtsReportResponse = zod_1.z.object({
    companyId: common_1.uuid,
    asOf: common_1.isoDate,
    party: zod_1.z.enum(['customers', 'suppliers']),
    rows: zod_1.z.array(zod_1.z.object({
        partyId: common_1.uuid,
        code: zod_1.z.string(),
        nameAr: zod_1.z.string(),
        ...debtFigures,
    })),
    /** مجموع الصفوف الظاهرة بعد التصفية. */
    totals: zod_1.z.object(debtFigures),
    /** إجمالى الأعمار قبل التصفية، ورصيد الحساب الرقابى والفرق — من تقرير الأعمار نفسه. */
    agingTotal: common_1.moneyString,
    controlBalance: common_1.moneyString,
    unreconciled: common_1.moneyString,
    hiddenCount: zod_1.z.number().int(),
});
//# sourceMappingURL=reports-center.js.map